// src/pages/platform-admin/PlatformAdminWorkView.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Chip,
    Grid,
    Slide,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tabs,
    Tab,
    Avatar,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Zoom,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    LinearProgress,
    Badge,
    useMediaQuery,
    TextField,
    InputAdornment,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Divider,
    Rating
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Description,
    HealthAndSafety,
    MenuBook,
    Article,
    Storage,
    Slideshow,
    Photo,
    Attachment,
    PictureAsPdf,
    InsertDriveFile,
    Download,
    Warning,
    Search,
    FilterList as FilterIcon,
    ClearAll,
    DateRange,
    School,
    Person,
    Email,
    Visibility,
    Close,
    ExpandMore,
    CheckCircle,
    Cancel,
    Schedule,
    Pending,
    Business,
    SupervisorAccount,
    Assignment as AssignmentIcon,
    Feedback,
    Category
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchWorkList,
    APIResearchWorkShow,
    APIUniversityList
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import authService from '../../apiServices/authService';
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import SupervisorBar from '../../components/SupervisorBar';
import StudentBar from '../../components/StudentBar';
import InstitutionHeadBar from '../../components/InstitutionHeadBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to get initials
const getInitials = (name) => {
    if (!name) return 'N/A';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get supervisor name
const getSupervisorName = (work) => {
    if (!work) return '';
    return work.supervisor_name || work.latest_review?.supervisor_name || '';
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PlatformAdminWorkView = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userType = authService.getUserType();

    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [works, setWorks] = useState([]);
    const [filteredWorks, setFilteredWorks] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWork, setSelectedWork] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [stats, setStats] = useState(null);
    const isMounted = useRef(true);

    // Filters
    const [filters, setFilters] = useState({
        work_type: '',
        work_status: '',
        university_id: '',
        specialty_id: '',
        dateFrom: '',
        dateTo: ''
    });
    const [filterOptions, setFilterOptions] = useState(false);
    const [universities, setUniversities] = useState([]);

    // Work type options for filtering
    const workTypes = [
        { value: 'proposal', label: 'Research Proposal', icon: <Description />, color: 'primary' },
        { value: 'ethics_application', label: 'Ethics Application', icon: <HealthAndSafety />, color: 'secondary' },
        { value: 'chapter', label: 'Thesis Chapter', icon: <MenuBook />, color: 'info' },
        { value: 'full_thesis', label: 'Full Thesis', icon: <Article />, color: 'success' },
        { value: 'dataset', label: 'Dataset', icon: <Storage />, color: 'warning' },
        { value: 'presentation', label: 'Presentation', icon: <Slideshow />, color: 'error' },
        { value: 'poster', label: 'Poster', icon: <Photo />, color: 'default' },
        { value: 'supplementary_material', label: 'Supplementary Material', icon: <Attachment />, color: 'info' }
    ];

    // Work status options
    const workStatusOptions = [
        { value: 'uploaded', label: 'Uploaded', color: 'info', icon: <Description /> },
        { value: 'submitted', label: 'Submitted', color: 'warning', icon: <Schedule /> },
        { value: 'under_review', label: 'Under Review', color: 'info', icon: <Pending /> },
        { value: 'revision_requested', label: 'Revision Requested', color: 'warning', icon: <Pending /> },
        { value: 'approved', label: 'Approved', color: 'success', icon: <CheckCircle /> },
        { value: 'rejected', label: 'Rejected', color: 'error', icon: <Cancel /> }
    ];

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchWorks();
        fetchStats();
        fetchUniversities();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        applyFilters();
    }, [works, searchTerm, filters, activeTab]);

    const fetchWorks = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.list(APIResearchWorkList);
            if (result.success && isMounted.current) {
                setWorks(Array.isArray(result.data) ? result.data : []);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load research works");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchStats = async () => {
        try {
            // Calculate stats from works data
            const totalWorks = works.length;
            const submittedWorks = works.filter(w => w.work_status === 'submitted' || w.work_status === 'under_review').length;
            const revisionWorks = works.filter(w => w.work_status === 'revision_requested').length;
            const approvedWorks = works.filter(w => w.work_status === 'approved').length;
            const rejectedWorks = works.filter(w => w.work_status === 'rejected').length;
            const totalStudents = [...new Set(works.map(w => w.student_id))].length;
            const totalUniversities = [...new Set(works.map(w => w.university_id))].filter(Boolean).length;
            
            setStats({
                total_works: totalWorks,
                submitted_works: submittedWorks,
                revision_works: revisionWorks,
                approved_works: approvedWorks,
                rejected_works: rejectedWorks,
                total_students: totalStudents,
                total_universities: totalUniversities
            });
        } catch (error) {
            console.error("Error calculating stats:", error);
        }
    };

    const fetchUniversities = async () => {
        try {
            // Fetch universities from API
            const result = await apiService.list(APIUniversityList);
            if (result.success && isMounted.current) {
                setUniversities(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Failed to fetch universities:", error);
        }
    };

    // Get unique specialties from works for filter dropdown
    const getUniqueSpecialties = () => {
        const specialties = new Map();
        works.forEach(work => {
            if (work.specialty_id && work.specialty_name) {
                specialties.set(work.specialty_id, {
                    id: work.specialty_id,
                    name: work.specialty_name,
                    code: work.specialty_code
                });
            }
        });
        return Array.from(specialties.values());
    };

    const applyFilters = () => {
        let filtered = [...works];
        
        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(work =>
                work.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.upload_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.university_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.specialty_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getSupervisorName(work)?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Work type filter
        if (filters.work_type) {
            filtered = filtered.filter(work => work.work_type === filters.work_type);
        }
        
        // Work status filter
        if (filters.work_status) {
            filtered = filtered.filter(work => work.work_status === filters.work_status);
        }
        
        // University filter
        if (filters.university_id) {
            filtered = filtered.filter(work => work.university_id === parseInt(filters.university_id));
        }
        
        // Specialty filter
        if (filters.specialty_id) {
            filtered = filtered.filter(work => work.specialty_id === parseInt(filters.specialty_id));
        }
        
        // Date filter
        if (filters.dateFrom) {
            filtered = filtered.filter(work => 
                work.uploaded_at && work.uploaded_at.split('T')[0] >= filters.dateFrom
            );
        }
        if (filters.dateTo) {
            filtered = filtered.filter(work => 
                work.uploaded_at && work.uploaded_at.split('T')[0] <= filters.dateTo
            );
        }
        
        // Tab filter
        if (activeTab === 1) {
            filtered = filtered.filter(work => work.work_status === 'submitted' || work.work_status === 'under_review');
        } else if (activeTab === 2) {
            filtered = filtered.filter(work => work.work_status === 'revision_requested');
        } else if (activeTab === 3) {
            filtered = filtered.filter(work => work.work_status === 'approved');
        } else if (activeTab === 4) {
            filtered = filtered.filter(work => work.work_status === 'rejected');
        }
        
        setFilteredWorks(filtered);
        setPage(0);
    };

    const handleOpenViewDialog = async (work) => {
        setSelectedWork(work);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedWork(null);
    };

    const handleDownload = (work) => {
        if (work.file_url) {
            window.open(work.file_url, '_blank');
        }
    };

    const getWorkTypeIcon = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.icon || <InsertDriveFile />;
    };

    const getWorkTypeColor = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.color || 'default';
    };

    const getWorkTypeLabel = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.label || type;
    };

    const getWorkStatusColor = (status) => {
        const found = workStatusOptions.find(s => s.value === status);
        return found?.color || 'default';
    };

    const getWorkStatusLabel = (status) => {
        const found = workStatusOptions.find(s => s.value === status);
        return found?.label || status;
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, dateFrom: today, dateTo: today }));
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            work_type: '',
            work_status: '',
            university_id: '',
            specialty_id: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setActiveTab(0);
        setFilterOptions(false);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return <LoadingState message="Loading Research Works..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchWorks} title="Unable to Load Research Works" variant="research" fullPage retryText="Retry Loading" />;
    }

    const statusTabs = [
        { value: 0, label: 'All Uploads', count: works.length, icon: <Description /> },
        { value: 1, label: 'Pending Review', count: works.filter(w => w.work_status === 'submitted' || w.work_status === 'under_review').length, icon: <Schedule /> },
        { value: 2, label: 'Revision Needed', count: works.filter(w => w.work_status === 'revision_requested').length, icon: <Pending /> },
        { value: 3, label: 'Approved', count: works.filter(w => w.work_status === 'approved').length, icon: <CheckCircle /> },
        { value: 4, label: 'Rejected', count: works.filter(w => w.work_status === 'rejected').length, icon: <Cancel /> }
    ];

    const determinTopBar = () => {
        if (userType === "institution_admin") {
            return <InstitutionAdminBar />;
        } else if (userType === "supervisor") {
            return <SupervisorBar />;
        } else if (userType === "institution_head") {
            return <InstitutionHeadBar />;
        } else if (userType === "student") {
            return <StudentBar />;
        } else {
            return <PlatformAdminBar notificationCount={5} />;
        }
    }
    

    return (
        <FullWidthContainer >
            <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                {/* <PlatformAdminBar notificationCount={5} /> */}
                {determinTopBar()}

                
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <AssignmentIcon /> Platform Research Works
                        </Typography>

                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={() => setFilterOptions(true)}
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                            >
                                View Options
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RefreshIcon />}
                                onClick={fetchWorks}
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Stats Cards */}
                    {stats && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={6} sm={3} md={2.4}>
                                <Card sx={{ textAlign: 'center', bgcolor: theme.palette.primary.light + '20' }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                        <Description sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                        <Typography variant="h6">{stats.total_works}</Typography>
                                        <Typography variant="caption">Total Works</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3} md={2.4}>
                                <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light + '20' }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                        <Schedule sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                                        <Typography variant="h6">{stats.submitted_works}</Typography>
                                        <Typography variant="caption">Pending Review</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3} md={2.4}>
                                <Card sx={{ textAlign: 'center', bgcolor: theme.palette.info.light + '20' }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                        <Pending sx={{ fontSize: 24, color: theme.palette.info.main }} />
                                        <Typography variant="h6">{stats.revision_works}</Typography>
                                        <Typography variant="caption">Revision Needed</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3} md={2.4}>
                                <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light + '20' }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                        <CheckCircle sx={{ fontSize: 24, color: theme.palette.success.main }} />
                                        <Typography variant="h6">{stats.approved_works}</Typography>
                                        <Typography variant="caption">Approved</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <Card sx={{ textAlign: 'center', bgcolor: theme.palette.grey[100] }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                        <Business sx={{ fontSize: 24, color: theme.palette.info.main }} />
                                        <Typography variant="h6">{stats.total_universities}</Typography>
                                        <Typography variant="caption">Universities</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {/* Search Bar */}
                    <Paper sx={{
                        mt: 2,
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                        backgroundColor: theme.palette.grey[50],
                    }}>
                        <Search sx={{ mx: 1, color: 'text.secondary' }} />
                        <TextField 
                            fullWidth 
                            placeholder="Search by title, student name, university, specialty, supervisor, or upload ID..." 
                            variant="standard" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            InputProps={{ disableUnderline: true }}
                            size="small"
                        />
                    </Paper>

                    {/* Active Filters */}
                    {(filters.work_type || filters.work_status || filters.university_id || filters.specialty_id || filters.dateFrom || filters.dateTo) && (
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                Active filters:
                            </Typography>
                            {filters.work_type && (
                                <Chip
                                    label={`Type: ${getWorkTypeLabel(filters.work_type)}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, work_type: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.work_status && (
                                <Chip
                                    label={`Status: ${getWorkStatusLabel(filters.work_status)}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, work_status: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.university_id && (
                                <Chip
                                    label={`University: ${universities.find(u => u.id === parseInt(filters.university_id))?.name || filters.university_id}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, university_id: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.specialty_id && (
                                <Chip
                                    label={`Specialty: ${getUniqueSpecialties().find(s => s.id === parseInt(filters.specialty_id))?.name || filters.specialty_id}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, specialty_id: '' }))}
                                    color="primary"
                                    variant="outlined"
                                    icon={<Category />}
                                />
                            )}
                            {filters.dateFrom && (
                                <Chip
                                    label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.dateTo && (
                                <Chip
                                    label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            <Button size="small" onClick={clearFilters} startIcon={<ClearAll />}>
                                Clear All
                            </Button>
                        </Box>
                    )}
                </Paper>

                {/* Status Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 100 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        {statusTabs.map(tab => (
                            <Tab 
                                key={tab.value}
                                icon={tab.icon}
                                label={
                                    <Badge badgeContent={tab.count} color="primary" sx={{ mr: 1 }}>
                                        {tab.label}
                                    </Badge>
                                } 
                                value={tab.value}
                            />
                        ))}
                    </Tabs>
                </Paper>
            </Box>

            {/* Works Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 1100, sm: 1200, md: 1400 } }}>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>University</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Specialty</TableCell>
                                    <TableCell>Supervisor</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Uploaded</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredWorks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((work, index) => (
                                    <TableRow
                                        key={work.id}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                                    {getInitials(work.student_name)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {work.student_name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Article fontSize="small" color="primary" />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {work.title?.substring(0, 50)}...
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {work.specialty_name ? (
                                                <Chip
                                                    icon={<Category />}
                                                    label={work.specialty_name}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    label="No Specialty"
                                                    size="small"
                                                    variant="outlined"
                                                    color="default"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SupervisorAccount fontSize="small" color="primary" />
                                                <Typography variant="body2">
                                                    {getSupervisorName(work) || 'Not Assigned'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getWorkTypeIcon(work.work_type)}
                                                label={getWorkTypeLabel(work.work_type)}
                                                size="small"
                                                color={getWorkTypeColor(work.work_type)}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={work.work_status_icon}
                                                label={work.formatted_work_status}
                                                size="small"
                                                color={work.work_status_color}
                                            />
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(work.uploaded_at)}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => handleOpenViewDialog(work)} color="primary" size="small">
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Download">
                                                    <IconButton onClick={() => handleDownload(work)} color="secondary" size="small">
                                                        <Download />
                                                    </IconButton>
                                                </Tooltip>
                                                {work.plagiarism_report_url && (
                                                    <Tooltip title="Plagiarism Report">
                                                        <IconButton href={work.plagiarism_report_url} target="_blank" size="small">
                                                            <Warning color="warning" fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredWorks.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredWorks.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <AssignmentIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No research works found</Typography>
                            <Typography variant="body2">Try adjusting your search query or filters</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={() => setFilterOptions(false)}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Works
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DateRange />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Uploads
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Work Type
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Work Type</InputLabel>
                            <Select
                                name="work_type"
                                value={filters.work_type}
                                onChange={handleFilterChange}
                                label="Work Type"
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {workTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {type.icon}
                                            {type.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Work Status</InputLabel>
                            <Select
                                name="work_status"
                                value={filters.work_status}
                                onChange={handleFilterChange}
                                label="Work Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {workStatusOptions.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {status.icon}
                                            {status.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by University
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>University</InputLabel>
                            <Select
                                name="university_id"
                                value={filters.university_id}
                                onChange={handleFilterChange}
                                label="University"
                            >
                                <MenuItem value="">All Universities</MenuItem>
                                {universities.map((university) => (
                                    <MenuItem key={university.id} value={university.id}>
                                        {university.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Specialty
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Research Specialty</InputLabel>
                            <Select
                                name="specialty_id"
                                value={filters.specialty_id}
                                onChange={handleFilterChange}
                                label="Research Specialty"
                            >
                                <MenuItem value="">All Specialties</MenuItem>
                                {getUniqueSpecialties().map((specialty) => (
                                    <MenuItem key={specialty.id} value={specialty.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Category />
                                            {specialty.name}
                                            {specialty.code && ` (${specialty.code})`}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setFilterOptions(false)} variant="contained">Close</Button>
                    <Button onClick={() => setFilterOptions(false)} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog
                open={openViewDialog}
                onClose={handleCloseViewDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ 
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Research Work Details</Typography>
                    <IconButton
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={handleCloseViewDialog}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedWork && (
                        <Box sx={{ p: 2 }}>
                            {/* Header Section */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                                    {selectedWork.upload_id}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {selectedWork.specialty_name && (
                                        <Chip
                                            icon={<Category />}
                                            label={selectedWork.specialty_name}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    <Chip
                                        icon={getWorkTypeIcon(selectedWork.work_type)}
                                        label={getWorkTypeLabel(selectedWork.work_type)}
                                        color={getWorkTypeColor(selectedWork.work_type)}
                                        variant="outlined"
                                    />
                                    <Chip
                                        icon={selectedWork.work_status_icon}
                                        label={selectedWork.formatted_work_status}
                                        color={selectedWork.work_status_color}
                                    />
                                </Box>
                            </Box>

                            {/* Work Information */}
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Work Information
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Title:</strong> {selectedWork.title}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Description:</strong> {selectedWork.description || 'Not provided'}
                                            </Typography>
                                        </Grid>
                                        {selectedWork.specialty_name && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Research Specialty:</strong>
                                                    <Chip 
                                                        icon={<Category />}
                                                        label={selectedWork.specialty_name}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ ml: 1 }}
                                                    />
                                                    {selectedWork.specialty_code && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            (Code: {selectedWork.specialty_code})
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Grid>
                                        )}
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Version:</strong> v{selectedWork.version_number}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>File Size:</strong> {selectedWork.file_size_mb} MB
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Word Count:</strong> {selectedWork.word_count?.toLocaleString() || 'Not specified'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Student Information */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <School sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Student Information
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Name:</strong> {selectedWork.student_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Registration ID:</strong> {selectedWork.student_registration_id}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Email:</strong> {selectedWork.student_email}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* University & Supervisor Information */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        University & Supervisor Information
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>University:</strong> {selectedWork.university_name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Supervisor:</strong> 
                                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                                    <SupervisorAccount fontSize="small" color="primary" />
                                                    {getSupervisorName(selectedWork) || 'Not Assigned'}
                                                </Box>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Plagiarism Information */}
                            {selectedWork.plagiarism_score && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Plagiarism Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Plagiarism Score:</strong> {selectedWork.plagiarism_score}%
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={selectedWork.plagiarism_score}
                                                    color={selectedWork.plagiarism_score > 30 ? 'error' : selectedWork.plagiarism_score > 15 ? 'warning' : 'success'}
                                                    sx={{ height: 8, borderRadius: 4, mt: 1 }}
                                                />
                                            </Grid>
                                            {selectedWork.plagiarism_report_url && (
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Download />}
                                                        href={selectedWork.plagiarism_report_url}
                                                        target="_blank"
                                                        size="small"
                                                    >
                                                        Download Plagiarism Report
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Review Information */}
                            {selectedWork.latest_review && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Review Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Reviewer:</strong> {selectedWork.latest_review.supervisor_name}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Review Comments:</strong>
                                                </Typography>
                                                <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], mt: 1 }}>
                                                    <Typography variant="body2">
                                                        {selectedWork.latest_review.review_comments}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            {selectedWork.latest_review.score && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Score:</strong> {selectedWork.latest_review.score}/100
                                                    </Typography>
                                                    <Rating 
                                                        value={selectedWork.latest_review.score / 20} 
                                                        readOnly 
                                                        precision={0.5}
                                                        sx={{ mt: 1 }}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Review Date:</strong> {formatCreatedAtDate(selectedWork.latest_review.review_submitted_at)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Revision Feedback */}
                            {selectedWork.revision_feedback && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                            <Feedback sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Revision Feedback
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedWork.revision_feedback}
                                        </Alert>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Dates */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Important Dates
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Uploaded:</strong> {formatCreatedAtDate(selectedWork.uploaded_at)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Last Modified:</strong> {formatCreatedAtDate(selectedWork.last_modified)}
                                            </Typography>
                                        </Grid>
                                        {selectedWork.submitted_at && (
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <strong>Submitted:</strong> {formatCreatedAtDate(selectedWork.submitted_at)}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {selectedWork.review_deadline && (
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <strong>Review Deadline:</strong> {formatCreatedAtDate(selectedWork.review_deadline)}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Download Section */}
                            {selectedWork.file_url && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<Download />}
                                        onClick={() => handleDownload(selectedWork)}
                                        fullWidth
                                    >
                                        Download Research Work
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseViewDialog} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </FullWidthContainer>
    );
};

export default PlatformAdminWorkView;